import React, { useRef, useEffect, useState } from 'react';
import maplibregl from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import { useFullScreenHandle } from 'react-full-screen'
import { StyleFullScreen, MapWrapper } from '../style/map_components';
import '../style/legend_map.css';
import { PMTILES_PREF_KEY } from '../config/config'
import { PMTILES_URL } from '../const/general'
import { BSN_TEL_LAYER_LIST, BSN_TEL_LAYER_KEY } from '../const/map_config'
import { setOfValRiverColor, setRiskRiverColor, setTelLevelStr } from '../func/color';
import MapFullscreenIcon from '../item/map_fullscreen_icon'
import MapSelectLayersIcon from '../item/map_select_layers_icon'
import MapRiverFloodLegendIcon from '../item/map_river_flood_legend_icon';
import bbox from '@turf/bbox';
import choropleth_bsn from '../data/bsn.json';
import choropleth_tel from '../data/tel.json'

const sourceSetting = {
    "osm": {
        "type": "raster",
        "tiles": ["https://cyberjapandata.gsi.go.jp/xyz/seamlessphoto/{z}/{x}/{y}.jpg"],
        "tileSize": 256,
        "attribution": "MapLibre | 地図の出典：<a href='https://maps.gsi.go.jp/development/ichiran.html' target='_blank'>国土地理院</a>"
    },
    "bsn": {
        "type": "vector",
        "tiles": [`${PMTILES_URL}/${PMTILES_PREF_KEY}/bsn/{z}/{x}/{y}.mvt`]
    },
    "tel": {
        "type": "geojson",
        "data": choropleth_tel
    }
};

// 背景地図の設定
const baseLayer = [
    {
        "id": "osm-tiles",
        "type": "raster",
        "source": "osm",
        "minzoom": 0,
        "maxzoom": 24
    }
];

// PMTilesから取得して表示、非表示を制御するレイヤ達
const otherLayers = [
    {
        "id": "bsn_outline",
        "source": "bsn",
        "source-layer": "bsn",
        "type": "line",
        "paint": {
            'line-color': '#ffffff',     // 線の色
            'line-width': [            // 枠線ように太めに設定する
                'interpolate',
                ['linear'],
                ['zoom'],
                3, 2.4,
                12, 4.4,
                20, 8.4
            ],
            'line-opacity': 1,         // 不透明度
            'line-dasharray': [2, 0],    // 点線パターン（例: [2px 線, 4px 空白]）
            'line-offset': 0             // 線のオフセット（上下位置ずらし）
        },
        "layout": {
            "visibility": "visible" // 最初から表示する場合は "visible"に
        }
    },
    {
        "id": "bsn",
        "source": "bsn",
        "source-layer": "bsn",
        "type": "line",
        "paint": {
            'line-color': '#5c9fed',     // 線の色
            'line-width': [            // 線の太さ（ピクセル）
                'interpolate',
                ['linear'],
                ['zoom'],
                3, 2,
                12, 4,
                20, 8
            ],
            'line-opacity': 1,         // 不透明度
            'line-dasharray': [2, 0],    // 点線パターン（例: [2px 線, 4px 空白]）
            'line-offset': 0             // 線のオフセット（上下位置ずらし）
        },
        "layout": {
            "visibility": "visible" // 最初から表示する場合は "visible"に
        }
    },
    {
        "id": "tel",
        "source": "tel",
        "type": "circle",
        "paint": {
            'circle-radius': [
                'interpolate',
                ['linear'],
                ['zoom'],
                3, 3,
                8, 6,
                12, 10,
                15, 14,
                20, 18
            ],
            'circle-stroke-width': 1,
            'circle-stroke-color': '#ffffff',
            'circle-color': '#5c9fed',
            'circle-opacity': 1
        },
        "layout": {
            "visibility": "visible" // 最初から表示する場合は "visible"に
        }
    }
];

const MapComponent = (props) => {
    const mapContainer = useRef(null);
    const map = useRef(null);

    const [selectBsnLayerList, setSelectBsnLayerList] = useState(
        Object.fromEntries(BSN_TEL_LAYER_LIST.map(item => [item.category, true]))
    );

    // 監視用Ref
    const bsnDataRef = useRef(props.bsnData);
    const telDataRef = useRef(props.telData);
    const selectCitysRef = useRef(props.selectCitys);

    const mapStyle = {
        height: '100%',
        width: '100%',
        margin: '0 auto',
    }

    // 全画面表示切り替え
    const fullscreenHandle = useFullScreenHandle()

    const layersSetting = [...baseLayer, ...otherLayers]

    let bsnHoveredId = null
    let telHoveredId = null
    let bsnAvgHoveredId = null

    const checkData = (data) => {
        if (!data || data === undefined || data === null || data.length < 1) {
            return false
        }
        return true
    }
    // レイヤー選択切り替え
    const switchLayer = () => {
        if (map.current && map.current.isStyleLoaded()) {
            BSN_TEL_LAYER_LIST.map((item) => {
                if (selectBsnLayerList[item.category]) {
                    map.current.setLayoutProperty(item.category,
                        'visibility', 'visible');
                    if (item.category === BSN_TEL_LAYER_KEY.BSN) {
                        map.current.setLayoutProperty('bsn_outline',
                            'visibility', 'visible');
                        map.current.on('mouseenter', BSN_TEL_LAYER_KEY.BSN, (e) => { handleMouseMoveBsn(e) });
                        map.current.on('mousemove', BSN_TEL_LAYER_KEY.BSN, (e) => { handleMouseMoveBsn(e) });
                    }
                    if (item.category === BSN_TEL_LAYER_KEY.TEL) {
                        map.current.on('mouseenter', BSN_TEL_LAYER_KEY.TEL, (e) => { handleMouseMoveTel(e) });
                        map.current.on('mousemove', BSN_TEL_LAYER_KEY.TEL, (e) => { handleMouseMoveTel(e) });
                    }
                    map.current.on('mouseleave', item.category, handleMouseLeave);
                } else {
                    map.current.setLayoutProperty(item.category,
                        'visibility', 'none');
                    if (item.category === BSN_TEL_LAYER_KEY.BSN) {
                        map.current.setLayoutProperty('bsn_outline',
                            'visibility', 'none');
                        map.current.off('mouseenter', item.category, (e) => { handleMouseMoveBsn(e) });
                        map.current.off('mousemove', item.category, (e) => { handleMouseMoveBsn(e) });
                    }
                    if (item.category === BSN_TEL_LAYER_KEY.TEL) {
                        map.current.off('mouseenter', BSN_TEL_LAYER_KEY.TEL, (e) => { handleMouseMoveTel(e) });
                        map.current.off('mousemove', BSN_TEL_LAYER_KEY.TEL, (e) => { handleMouseMoveTel(e) });
                    }
                    map.current.off('mouseleave', item.category, handleMouseLeave);
                }
            })
        }
    }

    // レイヤの色分け
    const setLayerStyle = () => {
        // フィルター済みの河川データ
        if (map.current && map.current.isStyleLoaded()) {
            switchLayer()
            // 河川レイヤの色分け
            setBsnStyle();
            // 水位計レイヤの色分け
            setTelStyle();

            // ズーム・センター調整
            if (checkData(bsnDataRef.current)) {
                setMapCenterZoom(BSN_TEL_LAYER_KEY.BSN, bsnDataRef.current.map(data => data.code));
            }
        }
    }

    // 河川スタイル変更
    const setBsnStyle = () => {
        // データがなければ透明化して終了
        if (!checkData(bsnDataRef.current)) {
            map.current.setPaintProperty(BSN_TEL_LAYER_KEY.BSN, 'line-color', 'rgba(0,0,0,0)');
            map.current.setPaintProperty("bsn_outline", 'line-color', 'rgba(0,0,0,0)');
            return;
        }

        // 色設定
        const selectedData = bsnDataRef.current

        const matchExpression_fill = ['match', ['get', 'bsn_code']];
        const matchExpression_line = ['match', ['get', 'bsn_code']];
        for (const item of selectedData) {
            matchExpression_fill.push(
                item.code,
                setOfValRiverColor(item.of_val)
            );
            matchExpression_line.push(item.code, '#ffffff');
        }
        matchExpression_fill.push('rgba(0,0,0,0)');
        matchExpression_line.push('rgba(0,0,0,0)');

        // 塗りf 
        map.current.setPaintProperty(BSN_TEL_LAYER_KEY.BSN, 'line-color', matchExpression_fill);
        map.current.setPaintProperty("bsn_outline", 'line-color', matchExpression_line);
    };

    // 水位計スタイル変更
    const setTelStyle = () => {
        // データがなければ透明化して終了
        if (!checkData(telDataRef.current)) {
            map.current.setPaintProperty(BSN_TEL_LAYER_KEY.TEL, 'circle-color', 'rgba(0,0,0,0)');
            map.current.setPaintProperty(BSN_TEL_LAYER_KEY.TEL, 'circle-stroke-color', 'rgba(0,0,0,0)');
            return;
        }
        // 塗り色を設定
        const selectedData = telDataRef.current
        const matchExpression_fill = ['match', ['get', 'tel_code']];
        const matchExpression_stroke = ['match', ['get', 'tel_code']];
        for (const item of selectedData) {
            matchExpression_fill.push(
                item.code,
                setRiskRiverColor(item.risk_val)
            );
            matchExpression_stroke.push(item.code, '#ffffff');
        }
        matchExpression_fill.push('rgba(0,0,0,0)');
        matchExpression_stroke.push('rgba(0,0,0,0)');

        // レイヤに適用
        map.current.setPaintProperty(BSN_TEL_LAYER_KEY.TEL, 'circle-color', matchExpression_fill);
        map.current.setPaintProperty(BSN_TEL_LAYER_KEY.TEL, 'circle-stroke-color', matchExpression_stroke);
    };

    // 地図のセンターとズーム調整
    const setMapCenterZoom = (selectLayer_category, ids) => {
        if (!map.current || ids.length === 0) return;

        const features = choropleth_bsn.features
        const featureTel = choropleth_tel.features

        // 対象IDの feature を抽出
        let filteredFeatures = features.filter(f =>
            ids.includes(f.properties.bsn_code)
        );

        if (filteredFeatures.length === 0) {
            filteredFeatures = featureTel.filter(f =>
                ids.includes(f.properties.tel_code)
            );
        }

        if (filteredFeatures.length === 0) return;

        // 抽出したフィーチャのコレクションを作成
        const filteredCollection = {
            type: "FeatureCollection",
            features: filteredFeatures
        };

        // Turf.js で bbox を取得
        const bounds = bbox(filteredCollection); // [west, south, east, north]

        map.current.fitBounds(bounds, {
            padding: 30,
            maxZoom: 13,   // 拡大制限
            duration: 800  // アニメーション時間
        });
    }

    // 河川レイヤーマウスホバー時の処理
    const handleMouseMoveBsn = (e) => {
        map.current.getCanvas().style.cursor = 'pointer';

        const feature = e.features[0];
        if (!feature) return;
        const id = feature.properties.bsn_code;

        // すでにホバー中なら無視
        if (bsnHoveredId === id) return;

        // データ未取得なら終了
        if (!checkData(bsnDataRef.current)) return

        // 一致する区域の中でリスクが最大のレコードを取得
        const dataList = bsnDataRef.current.filter(
            item => item.code === Number(id)
        );

        if (dataList.length === 0) return;

        const data = dataList.reduce((maxItem, currentItem) => {
            return currentItem.of_val > maxItem.of_val
                ? currentItem
                : maxItem;
        });
        // 前のポップアップを削除
        if (map.current.__hoverPopup) {
            map.current.__hoverPopup.remove();
            map.current.__hoverPopup = null;
        }

        bsnHoveredId = id;

        const popup = new maplibregl.Popup({
            closeButton: false,
            closeOnClick: false
        })
            .setLngLat(e.lngLat)
            .setHTML(`
                <div>${props.label}：<strong>${data.of_val}</strong></div>
                <div>流域１時間平均雨量：<strong>${data.h1_val} mm</strong></div>
                <div>河川名：<strong>${feature.properties.txt_river_name_show}</strong></div>
            `)
            .addClassName('custom-popup')
            .addTo(map.current);

        map.current.__hoverPopup = popup;
    };

    // 水位計レイヤーマウスホバー時の処理
    const handleMouseMoveTel = (e) => {
        map.current.getCanvas().style.cursor = 'pointer';
        const features = map.current.queryRenderedFeatures(e.point, {
            layers: ['tel']
        });
        if (!features.length) return;

        const feature = e.features[0];
        if (!feature) return;
        const id = feature.properties.tel_code;

        // すでにホバー中なら無視
        if (telHoveredId === id) return;
        // データ未取得なら終了
        if (!checkData(telDataRef.current)) return;

        const data = telDataRef.current.find(item => item.code === Number(id));
        if (!data) return;

        // ポップアップに表示する内容をまとめる
        const content = features.map(f => {
            const props = f.properties;
            return `
                <div>水位計名：<strong>${props.name}</strong></div>
                <div>河川名：<strong>${props.txt_river_name}</strong></div>
                <div>住所：<strong>${props.txt_tel_address}</strong></div>
                <div>水位(m)：<strong>${(data.mtr_val !== null) ? data.mtr_val + " m" : "-"}</strong></div>
                <div>水位(%)：<strong>${data.pct_val} %</strong></div>
                <div>水位危険度：<strong>${setTelLevelStr(data.risk_val)}</strong></div>
            `;
        }).join('<hr/>');

        // 前のポップアップを削除
        if (map.current.__hoverPopup) {
            map.current.__hoverPopup.remove();
            map.current.__hoverPopup = null;
        }

        telHoveredId = id;

        const popup = new maplibregl.Popup({
            closeButton: false,
            closeOnClick: false
        })
            .setLngLat(e.lngLat)
            .setHTML(content)
            .addClassName('custom-popup')
            .addTo(map.current);

        map.current.__hoverPopup = popup;
    };

    // レイヤーマウスアウト時の処理
    const handleMouseLeave = () => {
        map.current.getCanvas().style.cursor = '';
        if (map.current.__hoverPopup) {
            map.current.__hoverPopup.remove();
            map.current.__hoverPopup = null;
            bsnHoveredId = null
            telHoveredId = null
            bsnAvgHoveredId = null
        }
    };

    // 河川クリック時の処理
    const handleBsnClick = (e) => {
        const feature = e.features[0];
        const bsn_code = feature.properties.bsn_code;
        props.setSelectBsn([bsn_code]);
        props.setSelectTelCode([]);
        props?.setSelectMsh([]);
    };

    const handleTelClick = (e) => {
        const feature = e.features[0];
        const tel_code = feature.properties.tel_code;
        const msh_code = feature.properties.msh_code;
        props.setSelectBsn([]);
        props.setSelectTelCode([tel_code]);
        props?.setSelectMsh([msh_code]);
    };

    // ロード時の処理
    const handleLoad = () => {
        if (!map.current) return;
        // レイヤ色付け
        setLayerStyle();
        // 各種イベント設定
        map.current.on('click', BSN_TEL_LAYER_KEY.BSN, (e) => { handleBsnClick(e) });
        map.current.on('click', BSN_TEL_LAYER_KEY.TEL, (e) => { handleTelClick(e) });
    };

    // マップ初期化処理
    useEffect(() => {
        if (map.current) return;
        map.current = new maplibregl.Map({
            container: mapContainer.current,
            center: props.bsnMapCenter,
            zoom: props.bsnMapZoom,
            style: {
                version: 8,
                sources: sourceSetting,
                layers: layersSetting
            },
            attributionControl: false
        });
        map.current.addControl(new maplibregl.NavigationControl({
            // コンパス表示なし
            showCompass: false,
            showZoom: true,
        }), 'top-left');
        map.current.addControl(new maplibregl.AttributionControl({
            // Attributionは折りたたまない
            compact: false,
        }), 'bottom-right');
        map.current.on('load', handleLoad);

        return () => {
            if (!map.current) return;
            map.current.off('load', handleLoad);
            map.current.off('click', BSN_TEL_LAYER_KEY.BSN, (e) => { handleBsnClick(e) });
            map.current.off('click', BSN_TEL_LAYER_KEY.TEL, (e) => { handleTelClick(e) });
            map.current.off('mouseenter', BSN_TEL_LAYER_KEY.BSN, (e) => { handleMouseMoveBsn(e) });
            map.current.off('mousemove', BSN_TEL_LAYER_KEY.BSN, (e) => { handleMouseMoveBsn(e) });
            map.current.off('mouseleave', BSN_TEL_LAYER_KEY.BSN, handleMouseLeave);
            map.current.off('mouseenter', BSN_TEL_LAYER_KEY.TEL, (e) => { handleMouseMoveTel(e) });
            map.current.off('mousemove', BSN_TEL_LAYER_KEY.TEL, (e) => { handleMouseMoveTel(e) });
            map.current.off('mouseleave', BSN_TEL_LAYER_KEY.TEL, handleMouseLeave);
            map.current.getCanvas().removeEventListener('mouseleave', handleMouseLeave);
            map.current.remove();
            map.current = null;
        };
    }, []);

    // 市町村チェックボックス変更時に呼ばれる
    useEffect(() => {
        selectCitysRef.current = props.selectCitys
        if (props.selectTelCode.length > 0) props.setSelectTelCode([]);
        if (props?.selectMsh?.length > 0) props?.setSelectMsh([]);
        if (props.selectBsn.length > 0) props.setSelectBsn([]);

        // レイヤの色分け、中心・ズーム調整
        setLayerStyle()
    }, [props.selectCitys]);

    // 地域レイヤークリック時に呼ばれる
    // リセットボタンで個別のメッシュの選択が解除されたときも呼ばれる
    useEffect(() => {
        bsnDataRef.current = props.bsnData
        telDataRef.current = props.telData

        if (!map.current) return;
        const interval = setInterval(() => {
            if (map.current?.isStyleLoaded()) {
                //// TODO
                setLayerStyle();
                clearInterval(interval);
            }
        }, 100);

        return () => clearInterval(interval);
    }, [
        props.bsnData,
        props.telData,
        props.selectBsn,
        props.selectTelCode
    ]);

    // レイヤー切り替え時に呼ばれる
    useEffect(() => {
        setLayerStyle()
    }, [selectBsnLayerList]);

    return (
        <StyleFullScreen
            handle={fullscreenHandle}
            enabled={true}
        >
            <MapWrapper>
                <div
                    ref={mapContainer}
                    className=""
                    style={mapStyle}
                />
                <MapFullscreenIcon
                    fullscreenHandle={fullscreenHandle}
                    top={'70px'}
                />
                <MapSelectLayersIcon
                    selectLayerList={selectBsnLayerList}
                    setSelectLayerList={setSelectBsnLayerList}
                    pageType="river"
                />
                <MapRiverFloodLegendIcon
                    label={props.label}
                />
            </MapWrapper>
        </StyleFullScreen>
    );
};
export default MapComponent;